import { useEffect, useRef, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Lightbulb, Target, Clock, ExternalLink, Calendar, Mail, Rss } from 'lucide-react';
import { useScrollDepthTracking, useTimeOnPageTracking, useSectionTracking, trackEvent } from '@/hooks/useAnalytics';

const Insights = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [dots, setDots] = useState<{
    x: number;
    y: number;
    size: number;
    opacity: number;
    speed: number;
  }[]>([]);

  // State for Medium articles
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Analytics tracking
  useScrollDepthTracking([25, 50, 75, 90]);
  useTimeOnPageTracking();
  useSectionTracking('intro-section', 'Insights Intro');
  useSectionTracking('wayhome-case-study', 'Wayhome Case Study');
  useSectionTracking('medium-articles', 'Medium Articles');

  // Fetch Medium articles via RSS
  useEffect(() => {
    const fetchMediumArticles = async () => {
      try {
        setLoading(true);
        
        // Using CORS proxy to fetch Medium RSS feed
        const proxyUrl = 'https://api.allorigins.win/get?url=';
        const mediumFeed = 'https://medium.com/feed/@addyspiller_22941';
        
        const response = await fetch(proxyUrl + encodeURIComponent(mediumFeed));
        const data = await response.json();
        
        // Parse XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(data.contents, 'text/xml');
        const items = xmlDoc.querySelectorAll('item');
        
        const parsedArticles = Array.from(items).slice(0, 6).map(item => {
          const title = item.querySelector('title')?.textContent || '';
          const link = item.querySelector('link')?.textContent || '';
          const pubDate = item.querySelector('pubDate')?.textContent || '';
          const description = item.querySelector('description')?.textContent || '';
          
          // Helper function to estimate reading time from content
          const estimateReadingTime = (text: string) => {
            const wordsPerMinute = 220; // More realistic average reading speed
            const words = text.trim().split(/\s+/).length;
            
            // Since we're working with RSS excerpts (not full articles),
            // estimate the full article length based on the excerpt
            const excerptWords = words;
            let estimatedFullWords;
            
            // Estimate full article length based on excerpt length
            if (excerptWords < 50) {
              estimatedFullWords = 600; // Short excerpt = ~600 word article
            } else if (excerptWords < 100) {
              estimatedFullWords = 800; // Medium excerpt = ~800 word article  
            } else {
              estimatedFullWords = 1000; // Long excerpt = ~1000+ word article
            }
            
            const minutes = Math.ceil(estimatedFullWords / wordsPerMinute);
            return `${minutes} min read`;
          };

          // Extract text content from description for reading time calculation
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = description;
          const textContent = tempDiv.textContent || '';
          
          // Use specific excerpts and categories based on title
          let excerpt = '';
          let category = 'Product Strategy'; // default
          let readTime = estimateReadingTime(textContent); // Estimate from content length
          
          if (title.includes('Start Small, Build Smart')) {
            excerpt = 'Building a home buying decision tool reminded me why strong foundations — and smart MVPs — matter';
            category = 'MVP Strategy';
          } else if (title.includes(`Data Isn't Just Information`) || title.includes(`It's Trust`) || title.includes('Data Isn\'t Just Information') || title.includes('Data')) {
            excerpt = `When you're building a product, there's a temptation to capture all the data. More data = more insight, right?`;
            category = 'Data Strategy';
          } else if (title.includes('AI gave me a PRD') || title.includes('reality check')) {
            excerpt = 'I recently joined the MVP Club, a professional development community focused on the adoption of AI. Last week we kicked off a challenge to…';
            category = 'AI & Product Development';
          } else {
            // Use extracted content as excerpt
            excerpt = textContent.slice(0, 200) + '...' || '';
          }
          
          // Format the actual publication date
          const publishDate = new Date(pubDate);
          const formattedDate = publishDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          
          return {
            title,
            excerpt,
            category,
            readTime,
            date: formattedDate,
            url: link,
            isInternal: false
          };
        });
        
        // Always include the Wayhome case study as the featured content
        const wayhomeCase = {
          title: "Building Wayhome: When AI Meets User-Centered Design",
          excerpt: "The challenge of balancing rapid prototyping with strategic thinking—lessons from a month-long AI product challenge.",
          category: "Case Studies",
          readTime: "6 min read",
          date: "Featured",
          url: "#wayhome-case-study",
          isInternal: true,
          isFeatured: true
        };
        
        // Combine case study with Medium articles
        setArticles([wayhomeCase, ...parsedArticles]);
        setError(null);
      } catch (err) {
        console.error('Error fetching Medium articles:', err);
        setError('Unable to load latest articles');
        
        // Fallback content including your existing case study
        setArticles([
          {
            title: "Building Wayhome: When AI Meets User-Centered Design",
            excerpt: "The challenge of balancing rapid prototyping with strategic thinking—lessons from a month-long AI product challenge.",
            category: "Case Studies",
            readTime: "6 min read",
            date: "Featured",
            url: "#wayhome-case-study",
            isInternal: true,
            isFeatured: true
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchMediumArticles();
  }, []);

  useEffect(() => {
    // Generate static background dots
    const generateDots = () => {
      const newDots = [];
      for (let i = 0; i < 30; i++) {
        newDots.push({
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 3 + 1,
          opacity: Math.random() * 0.2 + 0.05,
          speed: Math.random() * 0.5 + 0.1
        });
      }
      setDots(newDots);
    };
    generateDots();

    // Set up scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '20px 0px',
        threshold: 0.01,
      }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section id="intro-section" className="relative bg-gradient-to-br from-white via-grayLight/30 to-cyanPrimary/5 text-darkText pt-24 md:pt-32 lg:pt-36 pb-16 md:pb-20 overflow-hidden">
        {/* Floating Dots Background */}
        <div className="absolute inset-0">
          {dots.map((dot, index) => (
            <div
              key={index}
              className="absolute rounded-full bg-cyanPrimary/20"
              style={{
                left: `${dot.x}%`,
                top: `${dot.y}%`,
                width: `${dot.size}px`,
                height: `${dot.size}px`,
                opacity: dot.opacity,
                animation: `float ${10 + dot.speed * 10}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>
        
        <div className="reveal-on-scroll relative z-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-darkText">
              Insights from the <span className="text-cyanPrimary">Product Trenches</span>
            </h1>
            <p className="text-xl text-darkText/80 leading-relaxed mb-8 max-w-3xl mx-auto">
              Real challenges from working with mission-driven product teams. Practical frameworks you can use immediately. Scroll down for a featured case study and the latest articles I've written about product strategy.
            </p>
          </div>
        </div>
        
        {/* Add CSS for floating animation */}
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            33% { transform: translateY(-10px) rotate(120deg); }
            66% { transform: translateY(5px) rotate(240deg); }
          }
        `}</style>
      </section>

      {/* Wayhome Case Study - Combined */}
      <section id="wayhome-case-study" className="section bg-white text-darkText">
        <div className="reveal-on-scroll">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-cyanPrimary text-white text-sm font-medium px-4 py-2 rounded-full mb-4">
                Featured Project
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-darkText">
                Building Wayhome: A GenAI Product Experiment
              </h2>
              <p className="text-xl text-darkText/80 max-w-3xl mx-auto">
                When I joined a month-long AI product challenge with professionals from various backgrounds, I got caught up in the excitement of rapid prototyping capabilities. Read on to hear where I faltered and how I leveraged my customer-centric product development process to build it back up.
              </p>
            </div>

            {/* Project Structure */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Project Overview */}
              <div className="bg-white rounded-2xl p-6 shadow-card text-left">
                <h3 className="text-lg font-semibold text-darkText mb-4 flex items-center gap-2">
                  <span className="bg-cyanPrimary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                  Project Overview
                </h3>
                <p className="text-darkText/80 text-sm leading-relaxed">
                  The tool I set out to build was simple in concept but tricky in execution: a prototype that ranks real estate listings based on a user's preferences — a scrappy MVP of a home-buying decision support tool.
                </p>
              </div>

              {/* Challenge */}
              <div className="bg-white rounded-2xl p-6 shadow-card text-left">
                <h3 className="text-lg font-semibold text-darkText mb-4 flex items-center gap-2">
                  <span className="bg-cyanPrimary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                  Challenge
                </h3>
                <p className="text-darkText/80 text-sm leading-relaxed">
                  I had ChatGPT write my product requirements document, dropped that into Lovable and had a prototype in a few minutes. But somewhere between the prompts and the output, the soul of my idea had gotten lost. Furthermore, my initial plan was to use APIs to pull live listing data. But I quickly ran into a familiar early-stage problem: <strong>those APIs were expensive.</strong>
                </p>
              </div>

              {/* Approach */}
              <div className="bg-white rounded-2xl p-6 shadow-card text-left">
                <h3 className="text-lg font-semibold text-darkText mb-4 flex items-center gap-2">
                  <span className="bg-cyanPrimary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                  Approach
                </h3>
                <p className="text-darkText/80 text-sm leading-relaxed mb-4">
                  Stepping back to focus on actual user needs—even within this compressed timeline—led to a more valuable outcome. I took the time to define the target user and their pain points; map the data needs to what would drive real customer value; and clearly documented my requirements.
                </p>
              </div>

              {/* Results */}
              <div className="bg-white rounded-2xl p-6 shadow-card text-left">
                <h3 className="text-lg font-semibold text-darkText mb-4 flex items-center gap-2">
                  <span className="bg-cyanPrimary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                  Results
                </h3>
                <p className="text-darkText/80 text-sm leading-relaxed">
                  Instead of expensive APIs, I used real listing samples as a database. This constraint allowed me to showcase actual functionality rather than just promising features. <em>I used GenAI tools to execute my documented plan — ensuring they served my strategy, not the other way around.</em>
                </p>
              </div>
            </div>

            {/* The Demo */}
            <div className="bg-gradient-to-r from-cyanPrimary/10 to-cyanLight/10 rounded-2xl p-8 md:p-12 text-center mb-16">
              <h3 className="text-2xl font-bold text-darkText mb-6">See Wayhome in Action</h3>
              <p className="text-lg text-darkText/80 mb-8 max-w-2xl mx-auto">
                This prototype — a decision tool for first-time homebuyers — demonstrates personalized ranking based on what users actually prioritize when choosing where to live. Built with strategy first, AI acceleration second, it shows what's possible when you resist the urge to lead with cool technology.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="https://wayhome.lovable.app/" target="_blank" rel="noopener noreferrer">
                  <Button 
                    onClick={() => trackEvent('wayhome_demo_click', { 
                      event_category: 'case_study_engagement',
                      event_label: 'Wayhome Demo Button',
                      page_section: 'case_study'
                    })}
                    className="bg-cyanPrimary hover:bg-cyanLight text-white flex items-center gap-2 py-4 px-8 rounded-xl shadow-soft hover:shadow-md transition-all duration-300 transform hover:-translate-y-1"
                  >
                    Try Wayhome Demo <ArrowRight size={18} />
                  </Button>
                </a>
                <p className="text-sm text-darkText/60">Use ZIP code 10023 to start exploring</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles from Medium */}
      <section id="medium-articles" className="section bg-grayLight text-darkText">
        <div className="reveal-on-scroll">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-darkText">
                Latest Articles
              </h2>
              <p className="text-lg text-darkText/80 mb-8">
                Follow my writing on Medium for insights from working with mission-driven product teams, plus practical frameworks you can use immediately.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <a 
                  href="https://medium.com/@addyspiller_22941" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    onClick={() => trackEvent('medium_follow_articles_click', {
                      event_category: 'conversion',
                      event_label: 'Medium Follow from Articles',
                      page_section: 'articles'
                    })}
                    className="bg-cyanPrimary hover:bg-cyanLight text-white flex items-center gap-2 py-3 px-6 rounded-xl"
                  >
                    <Mail size={18} />
                    Follow on Medium
                  </Button>
                </a>
                <p className="text-sm text-darkText/60">
                  New insights published regularly
                </p>
              </div>
            </div>

            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyanPrimary mx-auto"></div>
                <p className="text-darkText/60 mt-4">Loading latest articles...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <p className="text-darkText/60 mb-4">{error}</p>
                <p className="text-sm text-darkText/40">Visit Medium to see all articles</p>
              </div>
            )}

            {!loading && articles.length > 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles
                    .filter(article => !article.isFeatured)
                    .slice(0, 6)
                    .map((article, index) => (
                    <Card key={index} className="bg-white shadow-card hover:shadow-premium transition-all duration-300 rounded-xl group">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium px-3 py-1 bg-cyanPrimary/10 text-cyanPrimary rounded-full">
                            {article.category}
                          </span>
                          <div className="flex items-center gap-1 text-darkText/60 text-xs">
                            <Clock size={12} />
                            {article.readTime}
                          </div>
                        </div>
                        <CardTitle className="text-lg group-hover:text-cyanPrimary transition-colors line-clamp-2">
                          {article.title}
                        </CardTitle>
                        <div className="flex items-center gap-1 text-darkText/60 text-xs">
                          <Calendar size={12} />
                          {article.date}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-darkText/80 text-sm line-clamp-3 mb-4">
                          {article.excerpt}
                        </p>
                        <a 
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-cyanPrimary hover:text-cyanLight font-medium text-sm transition-colors"
                          onClick={() => trackEvent('article_click', {
                            event_category: 'content_engagement',
                            event_label: article.title,
                            page_section: 'articles'
                          })}
                        >
                          Read on Medium <ExternalLink size={14} />
                        </a>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="text-center mt-12">
                  <a 
                    href="https://medium.com/@addyspiller_22941" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button 
                      variant="outline"
                      className="border-cyanPrimary text-cyanPrimary hover:bg-cyanPrimary hover:text-white"
                      onClick={() => trackEvent('view_all_medium_click', {
                        event_category: 'external_link',
                        event_label: 'View All Articles',
                        page_section: 'articles'
                      })}
                    >
                      View All Articles <ExternalLink size={16} className="ml-2" />
                    </Button>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Insights;